import { Link } from "react-router";
import { BarraPublica } from "../componentes/layout";
import { TrilhoDaEsteira } from "../componentes/landing/TrilhoDaEsteira";
import { useTitulo } from "../nucleo/titulo";

interface Etapa {
  numero: number;
  nome: string;
  oQueE: string;
  oQueVoceFaz: string;
}

const ETAPAS: readonly Etapa[] = [
  {
    numero: 1,
    nome: "Contrato e cadastro",
    oQueE:
      "A GEOvendas registra a sua empresa e o contrato assinado: CNPJ, razão social, cidade, segmento, ERP e os módulos contratados.",
    oQueVoceFaz:
      "Confere o cadastro da empresa e indica quem participa — o administrador do portal, o contato de TI e um key user por módulo. Uma vez só, não uma vez por formulário.",
  },
  {
    numero: 2,
    nome: "Infraestrutura",
    oQueE:
      "A trilha de TI: onde o GEOvendas vai rodar, como se chega ao banco do ERP, portas, VPN e janela de manutenção.",
    oQueVoceFaz:
      "Responde a trilha de infraestrutura e libera o acesso ao servidor. Credencial entregue aqui fica cifrada no cofre do portal, e toda revelação é registrada com autor, data e motivo.",
  },
  {
    numero: 3,
    nome: "Kickoff dos módulos",
    oQueE:
      "Uma trilha por módulo contratado — Força de Vendas, B2B, Analytics, CRM 360, Estoque, FVT, GuiaRep. Cada uma tem responsável e prazo próprios.",
    oQueVoceFaz:
      "Cada key user responde a trilha do seu módulo. O que já foi respondido em outra trilha aparece preenchido, para conferir em vez de redigitar.",
  },
  {
    numero: 4,
    nome: "Reunião de kickoff",
    oQueE:
      "A GEOvendas agenda a reunião com a sua equipe para revisar o kickoff e registrar as decisões.",
    oQueVoceFaz:
      "Acompanha a data, o link e, depois da reunião, consulta a ata e a gravação quando publicadas.",
  },
  {
    numero: 5,
    nome: "Execução e virada",
    oQueE:
      "Com o kickoff consolidado, a consultoria configura o ambiente, valida com a sua equipe e conduz a virada para produção.",
    oQueVoceFaz:
      "Acompanha o que está pendente, com quem está e desde quando — e responde só o que ainda falta.",
  },
];

const COMO_E_HOJE: readonly string[] = [
  "O formulário chega por e-mail, é preenchido fora do processo e volta por e-mail.",
  "O nome da empresa é redigitado até oito vezes, uma por módulo.",
  "A mesma pergunta aparece em duas trilhas, e as duas respostas divergem sem ninguém notar.",
  "“Está em anexo” — e o anexo não veio, ou veio solto, sem dizer de qual pergunta é.",
  "O prazo é combinado numa reunião e depois vira memória de quem estava lá.",
];

const COMO_FICA: readonly string[] = [
  "Um endereço só, com convite nominal e senha sua — nada de planilha circulando.",
  "O cadastro da empresa é feito uma vez e vale para todas as trilhas seguintes.",
  "Resposta dada em um módulo é aproveitada nos outros, e divergência entre trilhas é apontada antes de virar retrabalho.",
  "Cada anexo fica preso à pergunta que o pediu, com quem enviou e quando.",
  "O prazo fica na tela: a data de entrega prevista é calculada, não digitada.",
];

const AJUDA: ReadonlyArray<{ titulo: string; texto: string }> = [
  {
    titulo: "Um consultor responsável",
    texto:
      "Toda implantação tem um consultor da GEOvendas que conduz a esteira do começo ao fim. Ele continua sendo o caminho para dúvida de conteúdo, de escopo e de prazo — o portal não substitui essa conversa, ele faz os dois lados começarem do mesmo ponto.",
  },
  {
    titulo: "Pendência com dono e data",
    texto:
      "O que está parado aparece na sua tela dizendo quem precisa agir e desde quando. Cobrança deixa de depender de alguém lembrar de cobrar.",
  },
  {
    titulo: "Problema com o convite",
    texto:
      "O link do convite tem validade e é de uso único. Se expirou ou já foi usado, peça um novo a quem enviou — nunca repasse o seu link para outra pessoa. Esqueceu a senha depois de entrar? A própria tela de acesso resolve.",
  },
];

export function Landing() {
  useTitulo();

  return (
    <div className="publico">
      <BarraPublica />

      <main className="publico__conteudo">
        <section className="abertura" aria-labelledby="abertura-titulo">
          <div className="abertura__texto">
            <p className="olho">Portal de Implantação · GEOvendas</p>
            <h1 className="abertura__titulo" id="abertura-titulo">
              Do contrato assinado ao <strong>Kick Off</strong>, num lugar só.
            </h1>
            <p className="abertura__lead">
              Este é o portal onde a implantação do GEOvendas na sua empresa acontece:
              cadastro, infraestrutura, kickoff dos módulos contratados e a virada para
              produção. Você responde uma vez, acompanha o que falta e vê o prazo — sem
              caçar a última versão do formulário na caixa de entrada.
            </p>
            <div className="abertura__acoes">
              <Link to="/entrar" className="botao botao--grande" data-testid="entrar-principal">
                Entrar no portal
              </Link>
              <a href="#esteira" className="botao botao--fantasma botao--grande">
                Ver as cinco etapas
              </a>
            </div>
            <p className="abertura__nota">
              O acesso é criado pela GEOvendas. Você recebe um convite nominal por e-mail,
              com um link de uso único, e define a sua própria senha no primeiro acesso.
            </p>
          </div>

          <TrilhoDaEsteira />
        </section>

        <section className="secao" id="esteira" aria-labelledby="esteira-titulo">
          <div className="secao__cabecalho">
            <h2 className="secao__titulo" id="esteira-titulo">
              As cinco etapas, e o que cabe a você em cada uma
            </h2>
            <p className="secao__lead">
              A esteira é a mesma para todos os clientes. O que muda é o conteúdo — e o
              portal já sabe quais módulos a sua empresa contratou.
            </p>
          </div>

          <ol className="etapas">
            {ETAPAS.map((etapa) => (
              <li className="etapa" key={etapa.numero}>
                <span className="etapa__numero" aria-hidden="true">
                  {etapa.numero}
                </span>
                <div className="etapa__corpo">
                  <h3 className="etapa__nome">{etapa.nome}</h3>
                  <p className="etapa__texto">{etapa.oQueE}</p>
                  <p className="etapa__voce">
                    <span className="etapa__marcador">O que você faz</span>
                    {etapa.oQueVoceFaz}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="secao" id="mudanca" aria-labelledby="mudanca-titulo">
          <div className="secao__cabecalho">
            <h2 className="secao__titulo" id="mudanca-titulo">
              O que muda para quem já passou por uma implantação
            </h2>
            <p className="secao__lead">
              Nada aqui é novidade de processo: é o mesmo kickoff, feito no lugar certo.
            </p>
          </div>

          <div className="comparacao">
            <div className="comparacao__lado comparacao__lado--antes">
              <h3 className="comparacao__titulo">Como costuma ser</h3>
              <ul className="comparacao__lista">
                {COMO_E_HOJE.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="comparacao__lado comparacao__lado--depois">
              <h3 className="comparacao__titulo">Como é no portal</h3>
              <ul className="comparacao__lista">
                {COMO_FICA.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="secao" id="prazo" aria-labelledby="prazo-titulo">
          <div className="secao__cabecalho">
            <h2 className="secao__titulo" id="prazo-titulo">
              O prazo, dito por inteiro
            </h2>
            <p className="secao__lead">
              Os dias contratuais de implantação não começam a contar sozinhos na assinatura.
            </p>
          </div>

          <div className="formula" role="group" aria-label="Como a entrega prevista é calculada">
            <div className="formula__peca">
              <span className="formula__rotulo">Assinatura do contrato</span>
            </div>
            <span className="formula__operador" aria-hidden="true">
              e
            </span>
            <div className="formula__peca">
              <span className="formula__rotulo">Liberação do acesso ao servidor</span>
            </div>
            <span className="formula__operador" aria-hidden="true">
              e
            </span>
            <div className="formula__peca">
              <span className="formula__rotulo">Aceite do kickoff</span>
            </div>
            <span className="formula__operador formula__operador--destaque">
              o que ocorrer por último
            </span>
            <span className="formula__operador" aria-hidden="true">
              +
            </span>
            <div className="formula__peca">
              <span className="formula__rotulo">Dias contratuais</span>
            </div>
            <span className="formula__operador" aria-hidden="true">
              =
            </span>
            <div className="formula__peca formula__peca--resultado">
              <span className="formula__rotulo">Entrega prevista</span>
            </div>
          </div>

          <p className="secao__texto">
            O prazo só começa após a liberação do ambiente e a geração da tarefa de kickoff, na
            última dessas datas. Até lá a entrega prevista fica a definir. O portal calcula a
            data quando os dois marcos forem registrados; ninguém a digita à mão.
          </p>
        </section>

        <section className="secao" id="ajuda" aria-labelledby="ajuda-titulo">
          <div className="secao__cabecalho">
            <h2 className="secao__titulo" id="ajuda-titulo">
              Se você precisar de ajuda no meio do caminho
            </h2>
          </div>

          <div className="cartoes">
            {AJUDA.map((item) => (
              <article className="cartao" key={item.titulo}>
                <h3 className="cartao__titulo">{item.titulo}</h3>
                <p className="cartao__texto">{item.texto}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="chamada" aria-labelledby="chamada-titulo">
          <h2 className="chamada__titulo" id="chamada-titulo">
            Recebeu o convite? É por aqui.
          </h2>
          <p className="chamada__texto">
            Entre com o seu e-mail corporativo e a senha que você definiu no primeiro acesso.
          </p>
          <div className="chamada__acoes">
            <Link to="/entrar" className="botao botao--verde botao--grande">
              Entrar no portal
            </Link>
            <Link to="/recuperar" className="chamada__secundario">
              Esqueci a minha senha
            </Link>
          </div>
        </section>
      </main>

      <footer className="rodape-publico">
        <div className="rodape-publico__interna">
          <p className="rodape-publico__linha">
            <b>GEOvendas</b> · Portal de Implantação
          </p>
          <p className="nota-rodape m-0">
            Ambiente restrito a clientes e ao time da GEOvendas. As etapas são liberadas
            conforme cada uma entra no ar, e você só recebe convite para o que já está
            disponível. A GEOvendas nunca pede a sua senha por e-mail, telefone ou mensagem.
          </p>
        </div>
      </footer>
    </div>
  );
}
